import { DeliveryStatus } from '@hooks/types';

export enum ArtistRole {
  MAIN_ARTIST = 'main artist',
  FEATURED_ARTIST = 'featured artist',
  COMPOSER = 'composer',
  REMIXER = 'remixer',
  PRODUCER = 'producer',
  LYRICIST = 'lyricist',
  ORCHESTRA = 'orchestra',
  ARRANGER = 'arranger',
  CONDUCTOR = 'conductor',
  WRITER = 'writer',
  AUTHOR = 'author',
  ACTOR = 'actor',
  UNKNOWN = 'unknown',
}

// matches BE: https://ghe.spotify.net/atlas-system/atlas-view/blob/master/src/main/proto/v2/album.proto#L27
export enum MediaType {
  MEDIA_TYPE_AUDIO_ONLY = 'MEDIA_TYPE_AUDIO_ONLY',
  MEDIA_TYPE_VIDEO_ONLY = 'MEDIA_TYPE_VIDEO_ONLY',
  MEDIA_TYPE_UNKNOWN = 'MEDIA_TYPE_UNKNOWN', // should never be this, but including for completeness
  MEDIA_TYPE_MIXED = 'MEDIA_TYPE_MIXED', // should not be receiving these yet
}

export enum ApprovalStatus {
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
  PARTIALLY_APPROVED = 'PARTIALLY_APPROVED',
  NEEDS_APPROVAL = 'NEEDS_APPROVAL',
  EXPIRED = 'EXPIRED',
  NO_APPROVAL_NEEDED = 'NO_APPROVAL_NEEDED',
}

export type Artist = {
  name: string;
  uri: string;
  url: string;
  role: ArtistRole;
  avatarUri?: string;
  isVerified?: boolean;
  isOptedInToAPP?: boolean;
  approvalStatus?: ApprovalStatus;
  approvalDecisionAt?: string;
};

export type Track = {
  name: string;
  isrc: string;
  duration: number;
  discNumber: number;
  trackNumber: number;
  explicitLyrics: boolean;
  uri: string;
};

export type DeliveryError = {
  message: string;
  type: string;
};

export enum AssetType {
  DELIVERY_ASSET_TYPE_UNKNOWN = 'DELIVERY_ASSET_TYPE_UNKNOWN',
  DELIVERY_ASSET_TYPE_AUDIO = 'DELIVERY_ASSET_TYPE_AUDIO',
  DELIVERY_ASSET_TYPE_IMAGE = 'DELIVERY_ASSET_TYPE_IMAGE',
  DELIVERY_ASSET_TYPE_VIDEO = 'DELIVERY_ASSET_TYPE_VIDEO',
}

export enum TranscodingStatus {
  STATUS_UNKNOWN = 'STATUS_UNKNOWN',
  STATUS_SUCCESS = 'STATUS_SUCCESS',
  STATUS_FAILURE = 'STATUS_FAILURE',
  STATUS_PENDING = 'STATUS_PENDING',
}

export type AssetTranscodingStatus = {
  assetType: AssetType;
  status: TranscodingStatus;
};

export type Delivery = {
  action: string;
  deliveredAt: string;
  deliveryId: string;
  feedGid: string;
  feedName: string;
  productId: string;
  source: string;
  deliveryStatus: DeliveryStatus;
  deliveryErrorsAndTypes: DeliveryError[];
  assetTranscodingStatuses: AssetTranscodingStatus[];
};

export type Album = {
  name: string;
  artists: Artist[];
  uri: string;
  url: string;
  type: string;
  coverArtImage: string;
  tracks: Track[];
  labelName: string;
  licensorName: string;
  feedName: string;
  primaryReleaseId: string;
  upc: string;
  originalReleaseDate: string;
  pLine: string;
  cLine: string;
  courtesyLine: string;
  titleVersion: string;
  ean: string;
  feedGid: string;
  identifiers: Identifier[];
  mediaType: MediaType;
};

export type EffectiveAlbum = Album & {
  prerelease?: { coverArtStart: string; start: string };
  isProviderTestContent?: boolean;
};

export enum AvailabilityStatus {
  DELIVERED = 'DELIVERED',
  DELIVERED_FLAGGED = 'DELIVERED_FLAGGED',
  TAKEDOWN = 'TAKEDOWN',
  UNAVAILABLE_FLAGGED = 'UNAVAILABLE_FLAGGED',
  NOT_DELIVERED = 'NOT_DELIVERED',
}

export enum MusicVideoClearanceStatusType {
  MUSIC_VIDEO_CLEARANCE_STATUS_TYPE_UNKNOWN = 'CLEARANCE_REQUEST_STATUS_TYPE_UNKNOWN',
  MUSIC_VIDEO_CLEARANCE_STATUS_TYPE_CLEARED = 'CLEARANCE_REQUEST_STATUS_TYPE_CLEARED',
  MUSIC_VIDEO_CLEARANCE_STATUS_TYPE_NOT_CLEARED = 'CLEARANCE_REQUEST_STATUS_TYPE_NOT_CLEARED',
  MUSIC_VIDEO_CLEARANCE_STATUS_TYPE_IN_PROGRESS = 'CLEARANCE_REQUEST_STATUS_TYPE_IN_PROGRESS',
  MUSIC_VIDEO_CLEARANCE_STATUS_TYPE_CLEARED_BY_OVERRIDE = 'CLEARANCE_REQUEST_STATUS_TYPE_CLEARED_BY_OVERRIDE',
}

export type Patch = {
  clearanceRequestStatus: MusicVideoClearanceStatusType;
  country: string;
  isCleared: boolean;
  lastUpdated: string;
};

export type AvailabilityDetails = {
  deliveredStart: string;
  deliveredEnd: string;
  effectiveStart: string;
  effectiveEnd: string;
  status: AvailabilityStatus;
  patches: Patch[];
};

export type AlbumAvailabilityByTerritory = {
  [key: string]: AvailabilityDetails;
};

export type AlbumAvailabilityByRegion = {
  [key: string]: AlbumAvailabilityByTerritory[];
};

export type ArtistPin = {
  isBlocked: boolean;
  blockingArtist: Artist;
};

export type AlbumResponse = {
  deliveredData: Album;
  effectiveData: EffectiveAlbum;
  availability: AlbumAvailabilityByTerritory;
  deliveries: Delivery[];
  artistPin: ArtistPin;
};

export type Identifier = {
  type: IdentifierType | string;
  value: string;
};

export enum PrimaryInformationLabel {
  URI = 'URI',
  URL = 'URL',
  PRIMARY_RELEASE_ID = 'Primary release ID',
  MAIN_ARTIST_URI = 'Main artist URI',
  ISRC = 'ISRC',
}

export enum IdentifierType {
  UPC = 'UPC',
  EAN = 'EAN',
  JAN = 'JAN',
  PROPRIETARY_ID = 'Proprietary ID',
  CATALOG_NUMBER = 'Catalog Number',
  GRID = 'GRiD',
  VENDOR_ID = 'Vendor ID',
  ISRC = 'ISRC',
  ISWC = 'ISWC',
  URI = 'URI',
  URL = 'URL',
  RELATED_RELEASE_ISRC = 'Related release ISRC',
}

// IdentifierOmittedType list provided by COPS @kerilynf
export enum IdentifierOmittedType {
  FAKE_ID = 'fake-id',
  FILIPE_TEST_FEED_ID = 'filipe-test-feed-id',
  GMB = 'gmb',
  GMB_ID = 'gmb-id',
  SPOTIFY_ASIA = 'spotify-asia',
  SPOTIFY_ASIA_ID = 'spotify-asia-id',
}
